import { DownArrowIcon } from "@/assets/images/svgIcons/arrowIcon"
import { Button } from "@/components/ui/baseUi/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { Text } from "@/components/ui/baseUi/text"
import { LoadingBlock } from "@/modules/boxDashboard/components/loadingBlock"
import { useWeeklyTrialsDropIns } from "@/modules/boxDashboard/modules/TrialsDropIns/hooks/useWeeklyTrialsDropIns"
import {
  type BirthdayMemberModel,
  type SlotParticipantModel,
  createWeeklyTrialsDropInsModel,
} from "@/modules/boxDashboard/modules/TrialsDropIns/models/weeklyTrialsDropInsModel"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { Language } from "@/modules/language/utils/languageUtils"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import dayjs from "dayjs"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { startTransition, Suspense, useMemo, useState } from "react"

export const WeeklyTrialsDropInsBlock = () => {
  const [weekOffset, setWeekOffset] = useState(0)
  const locale = useLanguage()

  // Calculate week dates based on offset
  const { startDate, endDate } = useMemo(() => {
    const today = dayjs()

    // For English US: week starts on Sunday (dayjs default)
    // For other locales: week starts on Monday
    let startOfWeek: dayjs.Dayjs
    if (locale === Language.ENGLISH_US) {
      startOfWeek = today.startOf("week") // Sunday
    } else {
      // Get Monday of current week
      const dayOfWeek = today.day() // 0 = Sunday, 1 = Monday, etc.
      const daysFromMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
      startOfWeek = today.add(daysFromMonday, "day").startOf("day")
    }

    const targetStart = startOfWeek.add(weekOffset, "week")
    const targetEnd = targetStart.add(6, "days")

    return {
      startDate: targetStart.format("YYYY-MM-DD"),
      endDate: targetEnd.format("YYYY-MM-DD"),
    }
  }, [weekOffset, locale])

  const model = useMemo(() => {
    return createWeeklyTrialsDropInsModel(null, locale, startDate, endDate)
  }, [locale, startDate, endDate])

  const handlePreviousWeek = () => startTransition(() => setWeekOffset((prev) => prev - 1))
  const handleNextWeek = () => startTransition(() => setWeekOffset((prev) => prev + 1))

  return (
    <div className="rounded-4xl bg-surface-foreground p-6">
      {/* Header with title and week navigation */}
      <div className="mb-6 flex items-center justify-between">
        <Text fontStyle="HS">{word("boxDashboard.trialsDropIns.title")}</Text>

        <div className="flex items-center gap-1">
          <Button onClick={handlePreviousWeek} size="iconSm" variant="secondary" aria-label="Previous week">
            <ChevronLeft size={16} className="text-textOnSurface-default" color="currentColor" />
          </Button>

          <Text fontStyle="BM" className="min-w-30 text-center text-textOnSurface-default">
            {model.weekLabel}
          </Text>

          <Button onClick={handleNextWeek} size="iconSm" variant="secondary" aria-label="Next week">
            <ChevronRight size={16} className="text-textOnSurface-default" color="currentColor" />
          </Button>
        </div>
      </div>

      <Suspense fallback={<LoadingBlock className="h-49.5" />}>
        <WeekTable startDate={startDate} endDate={endDate} />
      </Suspense>
    </div>
  )
}

function WeekTable({ startDate, endDate }: { startDate: string; endDate: string }) {
  const locale = useLanguage()

  const { weekData } = useWeeklyTrialsDropIns(startDate, endDate)

  const model = useMemo(() => {
    return createWeeklyTrialsDropInsModel(weekData, locale, startDate, endDate)
  }, [weekData, locale, startDate, endDate])

  return (
    <div className="flex w-full gap-2">
      {/* Left column with row labels */}
      <div className="flex flex-col gap-1">
        {/* Empty space for header */}
        <div className="mb-2 h-11" />

        {/* Trial session label */}
        <div className="flex h-11 items-center">
          <Text fontStyle="LS" className="whitespace-nowrap text-textOnSurface-default">
            {word("boxDashboard.trialsDropIns.trialSession")} ({model.totalTrials})
          </Text>
        </div>

        {/* Drop in label */}
        <div className="flex h-11 items-center">
          <Text fontStyle="LS" className="whitespace-nowrap text-textOnSurface-default">
            {word("boxDashboard.trialsDropIns.dropIn")} ({model.totalDropIns})
          </Text>
        </div>

        {/* Birthday label */}
        <div className="flex h-11 items-center">
          <Text fontStyle="LS" className="whitespace-nowrap text-textOnSurface-default">
            {word("boxDashboard.trialsDropIns.birthday")} ({model.totalBirthdays})
          </Text>
        </div>
      </div>

      {/* Columns for each day */}
      {model.days.map((day, index) => (
        <div key={index} className="flex flex-1 flex-col gap-1">
          {/* Day header */}
          <div className="mb-2 flex flex-col items-center gap-1 py-1">
            <Text
              fontStyle="LM"
              className={day.isToday ? "text-textOnSurface-primary-subtle" : "text-textOnSurface-default"}
            >
              {day.dayOfWeek}
            </Text>
            <Text
              fontStyle="LS"
              className={day.isToday ? "text-textOnSurface-primary-subtle" : "text-textOnSurface-default"}
            >
              {day.dayOfMonth}
            </Text>
          </div>

          {/* Trial indicator */}
          <div className="flex h-11 items-center justify-center">
            <DayIndicator count={day.numberOfTrials} hasData={day.hasTrials} slotParticipants={day.trials} />
          </div>

          {/* Drop-in indicator */}
          <div className="flex h-11 items-center justify-center">
            <DayIndicator count={day.numberOfDropIns} hasData={day.hasDropIns} slotParticipants={day.dropIns} />
          </div>

          {/* Birthday indicator */}
          <div className="flex h-11 items-center justify-center">
            <DayIndicator
              count={day.numberOfBirthdays}
              hasData={day.hasBirthdays}
              slotParticipants={day.birthdaysPresent}
              birthdaysNotPresent={day.birthdaysAbsent}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

type DayIndicatorProps = {
  count: number
  hasData: boolean
  slotParticipants: SlotParticipantModel[]
  birthdaysNotPresent?: BirthdayMemberModel[]
}

function DayIndicator({ count, hasData, slotParticipants, birthdaysNotPresent }: DayIndicatorProps) {
  const [open, setOpen] = useState(false)

  if (!hasData) {
    return (
      <div className="flex h-10 w-10 cursor-default items-center justify-center">
        <Text fontStyle="LS" className="text-textOnSurface-muted">
          -
        </Text>
      </div>
    )
  }

  const shouldShowPopover = slotParticipants.length > 0 || (birthdaysNotPresent && birthdaysNotPresent.length > 0)

  const indicator = (
    <div
      className={cn(
        "flex items-center justify-center rounded-full border border-interactive-border-neutral-default p-2 transition-colors",
        shouldShowPopover && "cursor-pointer hover:border-surface-background-primary-default",
      )}
      onMouseEnter={() => shouldShowPopover && setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <div className="flex h-4 w-4 items-center justify-center rounded-full bg-surface-background-primary-default">
        <Text fontStyle="LS" className="leading-none text-textDefault-body">
          {count}
        </Text>
      </div>
    </div>
  )

  if (!shouldShowPopover) {
    return indicator
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={indicator} />
      <PopoverContent
        className="scrollbar-textOnSurface-muted max-h-81.25 w-56 overflow-y-auto rounded-xl border-none bg-surface-background-float p-0"
        side="bottom"
        align="center"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <div className="flex flex-col">
          {slotParticipants.map((slotParticipant, index) => (
            <div
              key={slotParticipant.boxMemberId}
              className={cn(
                "flex flex-col px-2 py-1.5",
                index > 0 && "border-t border-interactive-border-neutral-disabled",
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <Text fontStyle="BM" className="truncate text-left text-textDefault-body">
                  {slotParticipant.athleteName}
                </Text>
                {slotParticipant.time && (
                  <Text fontStyle="BM" className="shrink-0 text-textDefault-body">
                    {slotParticipant.time}
                  </Text>
                )}
              </div>
              <div className="flex items-center justify-between gap-2">
                {slotParticipant.coachName && (
                  <Text fontStyle="BS" className="truncate text-left text-textDefault-caption">
                    {slotParticipant.coachName}
                  </Text>
                )}
                <Text fontStyle="BM" className="shrink-0 truncate text-right text-textDefault-body">
                  {slotParticipant.programName}
                </Text>
              </div>
            </div>
          ))}
          {birthdaysNotPresent && birthdaysNotPresent.length > 0 && (
            <>
              <div className="flex flex-row items-center justify-center gap-2 bg-surface-background p-2">
                <Text fontStyle="LS" className="text-textOnSurface-subtle">
                  {word("boxDashboard.trialsDropIns.notPresent")}
                </Text>
                <DownArrowIcon color="currentColor" className="size-3 text-textOnSurface-subtle" />
              </div>
              {birthdaysNotPresent.map((slotParticipant, index) => (
                <div
                  key={slotParticipant.boxMemberId}
                  className={cn(
                    "flex flex-col px-2 py-1.5",
                    index > 0 && "border-t border-interactive-border-neutral-disabled",
                  )}
                >
                  <Text fontStyle="BM" className="truncate text-left text-textDefault-body">
                    {slotParticipant.athleteName}
                  </Text>
                  <div className="flex flex-col items-end justify-end gap-2">
                    <Text fontStyle="BS" className="text-left text-textOnSurface-muted">
                      {word("boxDashboard.trialsDropIns.nextSession")} : {slotParticipant.nextSessionDate ?? "-"}
                    </Text>
                    <Text fontStyle="BS" className="text-right text-textOnSurface-muted">
                      {word("boxDashboard.trialsDropIns.lastSession")} :{" "}
                      {slotParticipant.lastSessionDateInDays
                        ? word("boxDashboard.trialsDropIns.lastSessionDateInDays", {
                            count: slotParticipant.lastSessionDateInDays,
                          })
                        : "-"}
                    </Text>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
